<template>
  <div class="relative mb-1 rounded-sm bg-gray-800 px-1 py-1 text-white hover:ring-2 hover:ring-yellow-500 agenda-event group-header cursor-pointer"
    :class="{ 'ring-2 ring-yellow-500': isOpen }"
    @click="$emit('toggle', !isOpen)">

    <div class="flex gap-2">

      <!-- STATUS BAR / TOGGLE (G + Arrow) -->
      <div class="w-3 flex flex-col items-center justify-between self-stretch rounded-sm transition-colors duration-200 pb-1" :class="bgStatusColor(eventStatus)">
         <span class="text-[10px] font-bold text-black mb-0.5">G</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-3 w-3 text-black transition-transform duration-200"
            :class="{ 'rotate-90': isOpen }"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor" stroke-width="3"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
          </svg>
      </div>

      <!-- CONTENT -->
      <div class="min-w-0 flex-1">

        <!-- ROW 1: TIMES + TITLE + OWNER -->
        <div class="flex flex-col md:flex-row md:items-center gap-4">
          <!-- TIMES -->
          <div class="flex gap-4 text-xs">
            <div v-for="col in timeColumns" :key="col.field">
              <div class="text-white/50">{{ col.headerName }}</div>
              <div class="font-semibold">
                {{ event[col.field] | dateUtc('HH:mm') }} <span class="text-white/35 font-normal">{{ event[col.field] | dateUtc('DD.MM') }}</span>
              </div>
            </div>
          </div>

          <!-- MAIN INFO -->
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2">
                <span class="truncate text-sm font-semibold" :class="eventStatusTextColorClass">
                    {{ customGroupName || commonName }}
                    <span class="text-white/60 ml-1">({{ eventGroup.length }})</span>
                </span>
                
            <div class="flex gap-1 ml-2">
                <span v-for="status in groupStatuses" :key="status" class="inline-block w-3 h-3 rounded-sm"
              :class="bgStatusColor(status)"></span>
            </div>
            <div v-if="eventProblems.length" class="ml-2 px-1 text-xs font-bold text-red-600 bg-white rounded-sm whitespace-nowrap">
                PROBLEMS: {{ eventProblems.join(', ') }}
            </div>
            </div>
            <div class="truncate text-xs text-white/45">
               {{ event.description }}
                <span v-for="col in otherColumns" :key="col.field">
                   <span class="mx-1">·</span>
                   <span v-if="col.type === 'html'" v-html="event[col.field]"></span>
                   <span v-else>{{ event[col.field] }}</span>
                </span>
            </div>
          </div>

          <!-- OWNER / CLIENT -->
          <div class="flex shrink-0 items-center gap-1 text-xs">
             <span v-if="commonAccountName" class="rounded-sm bg-black/60 px-2 py-0.5 text-emerald-300">{{ commonAccountName }}</span>
             <span class="rounded-sm bg-black/60 px-2 py-0.5 text-white/70">GROUP - {{ event.customer.name }}</span>
             <span v-if="eventTags.length > 0" class="flex gap-1 items-center">
                <span v-for="tag in eventTags" :key="tag" class="rounded-sm bg-blue-900/60 px-2 py-0.5 text-blue-200 text-[10px] uppercase">
                    {{ tag }}
                </span>
             </span>
             <span v-if="event.commentaryLanguage" class="rounded-sm bg-black/60 px-2 py-0.5 text-white/70">
                <i class="fas fa-globe-americas mr-1"></i> {{ event.commentaryLanguage }}
            </span>
          </div>
        </div>

        <!-- ROW 2: INPUTS + OUTPUTS -->
        <div class="mt-2 flex flex-col md:flex-row items-start gap-6">

          <!-- INPUTS (Aggregated) -->
          <div class="flex items-center" v-if="hasInputColumn">
             <span
              class="flex items-center gap-1.5 rounded-sm rounded-r-none bg-black/60 px-2 py-2 text-[11px] font-semibold text-white/55 h-full"
              title="Inputs"
            >
              <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" height="24" width="24">
                <g id="slow-motion-video">
                  <path id="Union" fill="currentColor" d="M7.0957 18.3174c1.10652 0.8602 2.44356 1.4359 3.9043 1.6181v2.0137c-2.01144 -0.1997 -3.84745 -0.9944 -5.3291 -2.207zM13 2.0498c5.0533 0.50175 9 4.76487 9 9.9502 0 5.1853 -3.9467 9.4474 -9 9.9492v-2.0137c3.9462 -0.4922 7 -3.856 7 -7.9355 0 -4.07955 -3.0538 -7.44434 -7 -7.93652zM4.06445 13c0.18215 1.4603 0.75741 2.797 1.61719 3.9033l-1.4248 1.4248C3.04464 16.8466 2.24948 15.011 2.0498 13zM16 12l-6 4V8zM5.68164 7.0957C4.8216 8.20212 4.24664 9.53946 4.06445 11H2.0498c0.1997 -2.01126 0.99458 -3.84751 2.20704 -5.3291zM11 4.06348c-1.46058 0.18217 -2.79784 0.75809 -3.9043 1.61816l-1.4248 -1.4248C7.15249 3.04438 8.98874 2.2495 11 2.0498z" stroke-width="1"></path>
                </g>
              </svg>
            </span>
            <div class="flex flex-wrap items-center gap-2 bg-black/40 px-2 py-1 rounded-sm rounded-l-none min-h-8">
                 <div v-for="(inputItem, idx) in uniqueInputs" :key="idx" class="flex items-center gap-1">
                      <div class="relative group" v-if="inputItem.thumb">
                        <img :src="inputItem.thumb" v-if="inputItem.thumb && (isSysAdmin || eventStatus === 'LIVE')" class="h-6 w-9 rounded-sm object-cover" />
                        <img :src="inputItem.thumb" v-if="inputItem.thumb && (isSysAdmin || eventStatus === 'LIVE')" class="absolute z-50 hidden w-52 group-hover:block top-0 left-0" style="max-width: none;" />
                      </div>
                      <span class="text-xs font-medium" v-html="inputItem.name"></span>
                 </div>
                  <span v-if="uniqueInputs.length === 0" class="text-xs text-white/45">No inputs</span>
            </div>
          </div>

          <!-- OUTPUTS (Aggregated) -->
          <div class="flex min-w-0 flex-1 items-start">
            <span
              class="flex items-center gap-1.5 rounded-sm rounded-r-none bg-black/60 px-2 py-2 text-[11px] font-semibold text-white/55 h-full"
              title="Outputs"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" height="24" width="24">
                <g id="output">
                  <path id="Union" fill="currentColor" d="M20.1025 3.00488C20.6067 3.05621 21 3.48232 21 4v3h-2V5H5v14h14v-2h2v3c0 0.5523 -0.4477 1 -1 1H4c-0.55228 0 -1 -0.4477 -1 -1V4l0.00488 -0.10254C3.05621 3.39333 3.48232 3 4 3h16zM21.207 11.293l0.0684 0.0761c0.3203 0.3928 0.2977 0.9718 -0.0684 1.3379l-4 4 -1.414 -1.414L18.0859 13H9v-2h9.0859L15.793 8.70703l1.414 -1.41406z" stroke-width="1"></path>
                </g>
              </svg>
            </span>
             <div class="flex min-w-0 flex-wrap gap-1.5">
               <!-- We are showing a summary of outputs -->
                <div v-for="(output, oIdx) in uniqueOutputs" :key="oIdx"
                     class="flex items-center gap-1 rounded-sm rounded-l-none bg-black/40 px-2 py-1 text-xs h-8">
                    <span class="font-medium">{{ output.displayName }}</span>
                     <span class="rounded-full px-1.5 text-[10px]"
                        :class="'output-type-tag ' + output.displayLabel"
                     >{{ output.displayLabel }}</span>
                     
                     <span class="rounded-full px-1.5 text-[10px]" v-if="output.status"
                        :class="'output-type-tag output-status-' + output.status"
                     >{{ output.status }}</span>

                     <!-- Icons -->
                      <span v-if="output.hasStreamKey">
                         <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-green-500"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" /></svg>
                     </span>
                     <span v-else-if="output.hasWowzaConfig">
                           <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-red-500"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" /></svg>
                     </span>
                     <span v-if="output.hasWowzaConfig && output.autostart" :title="`Offset ${output.autostartOffsetMin} min`">
                          <i class="bi bly-time green"></i>
                      </span>
                     <span v-if="output.hasWowzaConfig && !output.autostart">
                          <i class="bi bly-time red"></i>
                     </span>

                     <span v-if="output.ottServerString" class="text-[10px]" v-html="output.ottServerString"></span>
                     
                     <span v-if="output.isConnected !== null" class="w-2 h-2 rounded-full"
                        :class="output.isConnected ? 'bg-green-500' : 'bg-red-500'"></span>

                     <span v-if="output.override" class="text-[10px] text-amber-200" >(OTT: {{output.override}})</span>

                     <span v-if="output.count > 1" class="text-[10px] text-white/50 ml-1">x{{ output.count }}</span>
                </div>

                <div v-if="uniqueOutputs.length > 5" class="flex items-center gap-1 rounded-sm rounded-l-none bg-black/40 px-2 py-1 text-xs h-8 text-white/50">
                    ...
                </div>

                <span v-if="uniqueOutputs.length === 0" class="text-xs text-white/45">No outputs assigned</span>
             </div>
          </div>

        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import moment from 'moment'

const props = defineProps({
  event: {
    type: Object,
    required: true
  },
  eventGroup: {
    type: Array,
    required: true
  },
  groupEventsStatuses: {
    type: Array,
    default: () => []
  },
  visibleColumnDefs: {
    type: Array,
    default: () => []
  },
  isOpen: {
    type: Boolean,
    default: false
  },
  isMobileOnly: {
    type: Boolean,
    default: false
  },
  inputStatuses: {
    type: Object,
    default: () => ({})
  },
  isSysAdmin: {
    type: Boolean,
    default: false
  },
  streamManagerConnections: {
    type: Object,
    default: () => ({})
  },
  serverStatus: {  
      type: Object,
      default: () => ({})
  }
})

// --- Columns ---
const timeColumns = computed(() => {
    return props.visibleColumnDefs.filter(col => col.type === 'time');
});

const otherColumns = computed(() => {
    return props.visibleColumnDefs.filter(col => col.type !== 'time' && col.field !== 'inputName' && col.field !== 'outputUI' && col.field !== 'audioUI');
});

const hasInputColumn = computed(() => {
    return props.visibleColumnDefs.some(col => col.field === 'inputName');
});

// --- Status Logic ---
const groupStatuses = computed(() => {
  return [...new Set(props.groupEventsStatuses)]
})

const eventStatus = computed(() => {
  const statusOrder = [
    "PENDING",
    "CLIENT_CONFIRMED",
    "RECEIVED",
    "AWAITING_SIGNAL",
    "CONFIRMED",
    "LIVE",
    "FAILED",
    "COMPLETED",
    "LATE_CANCELLED",
  ];
  // Filter statuses to only existing ones
  const validStatuses = groupStatuses.value.filter(s => statusOrder.includes(s));
  if (validStatuses.length === 0) return props.event.status || 'PENDING';
  
  const sortedStatuses = validStatuses.sort((a, b) => statusOrder.indexOf(a) - statusOrder.indexOf(b));
  return sortedStatuses[0];
})

const customGroupName = computed(() => {
  if (props.event.groupId && props.event.groupId.includes(',')) {
    return props.event.groupId.split(',')[1];
  }
  return null;
})

const eventStatusTextColorClass = computed(() => {
  return `text-${eventStatus.value}`
})

const bgStatusColor = (status) => {
  return `bg-${status}`;
}

// --- Common Name Logic (Ported) ---
const commonName = computed(() => {
    const first = findCommonPartFirst();
    if (first) return first;
    return findCommonPartSecond() || props.event.name;
});

const eventsName = computed(() => {
    return props.eventGroup.map((e) => e.name);
});

const findCommonPartFirst = () => {
    if (eventsName.value.length === 0) return "";
    const shortest = eventsName.value.reduce((a, b) => (a.length <= b.length ? a : b));
    let commonPart = "";
    for (let i = 0; i < shortest.length; i++) {
        const char = shortest[i];
        if (eventsName.value.every((str) => str[i] === char)) {
            commonPart += char;
        } else {
            break;
        }
    }
    return commonPart;
};

const findCommonPartSecond = () => {
      if (eventsName.value.length === 0) return "";
      const reversedNames = eventsName.value.map((name) => name.split("").reverse().join(""));
      const shortest = reversedNames.reduce((a, b) => (a.length <= b.length ? a : b));
      let commonPart = "";
      for (let i = 0; i < shortest.length; i++) {
        const char = shortest[i];
        if (reversedNames.every((str) => str[i] === char)) {
          commonPart += char;
        } else {
          break;
        }
      }
      commonPart = commonPart.split("").reverse().join("");
      if (commonPart.match(/^[a-z] -/)) {
        commonPart = commonPart.substring(3);
      } else if (commonPart.match(/^[a-z]/)) {
        commonPart = commonPart.substring(1);
      }
      return commonPart;
};

// --- Common Account Name ---
const commonAccountName = computed(() => {
      if (!props.eventGroup || props.eventGroup.length === 0) return "";
      const firstAccount = props.eventGroup[0].account && props.eventGroup[0].account.name;
      if (!firstAccount) return "";
      const allSame = props.eventGroup.every((e) => e.account && e.account.name === firstAccount);
      return allSame ? firstAccount : "";
});


// --- Inputs Aggregation ---
const uniqueInputs = computed(() => {
    const inputsMap = new Map();

    props.eventGroup.forEach(ev => {
        if (ev.input) {
            const taskId = ev.input.probeTaskId || (ev.probes ? ev.probes.probeTaskId : null);
            const name = ev.input.name;
            const uniqueKey = name + '_' + taskId;
            
            if (!inputsMap.has(uniqueKey)) {
                // Get Thumb
                let thumbUrl = '';
                if (ev.inputOverride && ev.inputOverride.name && ev.inputOverride.probeTaskId) {
                     // Check override
                     const pid = ev.inputOverride.probeProjectId;
                     const tid = ev.inputOverride.probeTaskId;
                     if (props.inputStatuses && props.inputStatuses[pid] && props.inputStatuses[pid][tid]) {
                         thumbUrl = props.inputStatuses[pid][tid].thumb?.data;
                     }
                } else {
                     const pid = ev.input.probeProjectId;
                     const tid = taskId; 
                     
                     if (pid && tid && props.inputStatuses && props.inputStatuses[pid] && props.inputStatuses[pid][tid]) {
                         thumbUrl = props.inputStatuses[pid][tid].thumb?.data;
                     } else if (tid && props.inputStatuses && props.inputStatuses[34] && props.inputStatuses[34][tid]) {
                          // Fallback for hardcoded 34
                          thumbUrl = props.inputStatuses[34][tid].thumb?.data;
                     }
                }

                inputsMap.set(uniqueKey, {
                    name: name,
                    thumb: thumbUrl
                });
            }
        }
    });

    return Array.from(inputsMap.values());
});


// --- Outputs Aggregation ---
const uniqueOutputs = computed(() => {
    const outputsMap = new Map();

    props.eventGroup.forEach(ev => {
        if (ev.outputs && ev.outputs.items) {
             ev.outputs.items.filter(o => !!o).forEach(outputItem => {
                 const out = outputItem.output;
                 const key = out.displayName || out.name; 
                 
                 // Get Config
                 const [outputConfig] = (ev.actions && ev.actions.items)
                    ? ev.actions.items.filter((action) => action.assignedOutput && action.assignedOutput.id === out.id)
                    : [null];

                 // Determine properties
                 const hasWowzaConfig = outputConfig && outputConfig.configWowza;
                 let autostart = false;
                 let autostartOffsetMin = 0;
                 let hasStreamKey = false;
                 
                 if (hasWowzaConfig) {
                     autostart = outputConfig.configWowza.autostart;
                     autostartOffsetMin = outputConfig.configWowza.autostartOffsetMin || 0;
                     hasStreamKey = outputConfig.configWowza.targets && outputConfig.configWowza.targets.items && outputConfig.configWowza.targets.items[0] && outputConfig.configWowza.targets.items[0].streamKey;
                 }

                 // Connection Status
                 let isConnected = null;
                 if (props.isSysAdmin && out.smEndpoints) {
                     try {
                        const t = JSON.parse(out.smEndpoints);
                        const ids = t.map(sm => sm.interfaceId);
                        // Find first existing connection update
                        const connection = ids.map(id => props.streamManagerConnections ? props.streamManagerConnections[id] : null).find(c => c);
                         
                         if (connection) {
                             isConnected = (connection.outputs_connected !== '');
                         } else {
                             isConnected = null; 
                         }
                     } catch (e) {}
                 }

                 const ottServerString = out.ottServer && out.ottApplication ? `(${out.ottServer} - ${out.ottApplication})` : '';
                 const override = outputItem.outputOttApplicationOverride;


                 if (!outputsMap.has(key)) {
                     let displayLabel = out.outputType;
                     if (out.outputType === 'TECHEX') {
                        if (out.url && out.url.startsWith('srt')) displayLabel = 'CING-SRT';
                        else if (out.url && out.url.startsWith('rtp')) displayLabel = 'CING-RTP';
                        else displayLabel = 'CING';
                     }

                     outputsMap.set(key, {
                         displayName: out.displayName || out.name,
                         outputType: out.outputType,
                         displayLabel,
                         status: out.status, 
                         hasWowzaConfig,
                         autostart,
                         autostartOffsetMin,
                         hasStreamKey,
                         isConnected,
                         ottServerString,
                         override,
                         count: 0
                     });
                 }
                 outputsMap.get(key).count++;
             });
        }
    });


    return Array.from(outputsMap.values());
});

const eventProblems = computed(() => {
  if (!props.isSysAdmin) return []
  const problems = []
  
  if (!props.event.actions || !props.event.actions.items || props.event.actions.items.length === 0) {
    problems.push('ACTIONS')
  }
  if (!props.event.start) {
    problems.push('DATE')
  }
  if (!props.event.input) {
    problems.push('INPUT')
  }
  if (!props.event.outputs || !props.event.outputs.items || props.event.outputs.items.length === 0) {
    problems.push('OUTPUTS')
  }
  
  if (props.event.lineupTime && props.event.startTime && moment(props.event.lineupTime).isAfter(moment(props.event.startTime))) {
    problems.push('LINEUP > START')
  }
  if (props.event.startTime && props.event.endTime && moment(props.event.startTime).isAfter(moment(props.event.endTime))) {
    problems.push('START > END')
  }

  return problems
})

const eventTags = computed(() => {
    const tags = new Set();
    props.eventGroup.forEach(ev => {
        if (ev.tags && Array.isArray(ev.tags)) {
            ev.tags.forEach(tag => tags.add(tag));
        }
    });
    return Array.from(tags).sort();
});


</script>

<style scoped>
</style>
